import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  IconButton, 
  Typography, 
  Slider, 
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Menu,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  VolumeUp,
  VolumeOff,
  Fullscreen,
  FullscreenExit,
  SkipNext,
  SkipPrevious,
  Speed,
  Menu as MenuIcon,
  CheckCircle,
  FastForward,
  FastRewind,
  Description,
  Assignment,
  Quiz,
  Image,
  Close,
  PictureAsPdf,
  InsertDriveFile,
} from '@mui/icons-material';
import axios from 'axios';
import './ContentPlayer.css';
import DocumentViewer from './DocumentViewer';
import { useContentPlayer } from '../../context/ContentPlayerContext';
import { courseService, authService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { tokens } from '../../theme';
import FeedbackModal from '../Feedback/FeedbackModal';
const ContentPlayer = ({ 
    courseId: propCourseId, 
    contentId: propContentId, 
    isModal = false, 
    onClose = null,
    returnUrl = null,
    isLearnerMode = false,
    onComplete = null,
    isInline = false,
    contentLearnerRefId = null,
    lpathId = null,
    refreshOnClose,
    singleContent,
    hideCourseNavigation = false,
    showCloseButton = false,
    isSearchResult = false,
    seek: propSeek = null,
    queryId: propQueryId = null,
    byContentId = false,
  }) => {
  
  const { courseId: urlCourseId, contentId: urlContentId } = useParams();
 
  const params = useParams(); 
  const navigate = useNavigate();
  const { updateCurrentContent, markContentCompleted: contextMarkCompleted } = useContentPlayer();
  const finalCourseId = propCourseId || params.courseId;
  const finalContentId = propContentId || params.contentId;
  
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [courseContents, setCourseContents] = useState([]);
  const [currentContent, setCurrentContent] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [speedMenuAnchor, setSpeedMenuAnchor] = useState(null);
  const [isScorm, setIsScorm] = useState(false);
  const [isJustHovering, setIsJustHovering] = useState(false);
  const [hasStartedPlayback, setHasStartedPlayback] = useState(!isInline || isModal);
  // The item Play was pressed for in an inline player (its content ID, as every inline player is given), for the save
  // when an item leaves the player (N40.1 pt 12.2: before Play nothing is saved). The save's effect sees the item it was
  // set up for, so an item played before the next one loads is still saved, and the next one waits for its own Play.
  // Players that aren't inline start played.
  const playedContentIdRef = useRef(null);
  const wasPlayed = (content) => !isInline || isModal
    || Boolean(content && playedContentIdRef.current !== null && String(content.id) === playedContentIdRef.current);
  
  // Notification and completion state
  const [completionDialog, setCompletionDialog] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [contentCompleted, setContentCompleted] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  // track last-known play time for best-effort saves
  const latestTimeRef = useRef(0);
  const latestDurationRef = useRef(0);
  const savingRef = useRef(false);
  
  // State for "Was this helpful?" dialog (search intent resolution)
  const [helpfulDialogOpen, setHelpfulDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'exit', 'next', 'prev'
  
  // State for audience type access control
  const [isAccessDenied, setIsAccessDenied] = useState(false);
  const [accessDenialReason, setAccessDenialReason] = useState('');
  // keep current user's audience_type (retrieved from session via API)
  const [userAudienceType, setUserAudienceType] = useState(null);


  // Use the props if provided, otherwise use the URL params
  const courseId = propCourseId || params.courseId;
  const contentId = propContentId || params.contentId;

  // Determine content type properties early
  const contentType = currentContent?.type?.toLowerCase();
  // Audio files (MP3, M4A, WAV) play in the same media element as video, so they get the same
  // progress, seek-to-time and completion handling. Before this they showed "may not display properly".
  const isAudioFile = /\.(mp3|m4a|wav)(\?|$)/i.test(currentContent?.path || '');
  const isVideo = (currentContent?.type?.toLowerCase() === 'video' || currentContent?.type?.toLowerCase() === 'audio' || isAudioFile) &&
                 !(currentContent?.path?.includes('youtube.com/embed') || 
                   currentContent?.path?.includes('youtu.be'));
  // An uploaded video under an answer shows its frame and length before Play (N40.1 pt 12.2); audio has no frame.
  const isAudioMedia = isAudioFile || contentType === 'audio';
  const showFrameBeforePlay = isInline && !hasStartedPlayback && isVideo && !isAudioMedia;
  // A document (PDF, Word or text) opens; media plays (N40.1 pt 12.3).
  const isDocument = ['pdf', 'document', 'text'].includes(contentType) || /\.(pdf|docx?|txt)(\?|$)/i.test(currentContent?.path || '');
  // The content is complete for this learner: from this visit or from before.
  const isContentComplete = contentCompleted || currentContent?.completed_status === '1' || currentContent?.completed === true;
  const { showToast } = useToast();
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackData, setFeedbackData] = useState(null);
  const [isFeedbackEnabled, setIsFeedbackEnabled] = useState(false);  
  // Get search context from ContentPlayerContext for tracking helpful feedback
  const { modalState } = useContentPlayer();
  const [searchQuery, setSearchQuery] = useState(null);
  const [searchQueryId, setSearchQueryId] = useState(null);
  const [seek, setSeek] = useState(null);
  const completionInFlightRef = useRef(new Set());
  const completionNoticeShownRef = useRef(new Set());
  // What content leaving the player waits for (N40.1 pt 13): the completion call in flight, and the course's feedback
  // form while it shows (set as the form opens, so a close or move started then sees it). afterFeedbackRef holds the
  // close or move until the form closes; leavingRef lets one leave run at a time; leftCcrRef is the module whose leave
  // already ran, so unmounting after it does not run it again; scormFrameRef is the SCORM player's frame.
  const completionWorkRef = useRef(Promise.resolve());
  const feedbackOpenRef = useRef(false);
  const afterFeedbackRef = useRef(null);
  const leavingRef = useRef(false);
  const leftCcrRef = useRef(null);
  const scormFrameRef = useRef(null);
  const scormParamsRef = useRef({ key: null, params: '' });
  const isSearchView = isSearchResult || modalState.isSearchResult || Boolean(searchQuery || searchQueryId);
  // A search (in the app or the widget), History and recent content open the player with a content ID; the course and
  // path pages still pass the module's ID (until N40.1 pt 13). The player finds the module by the kind of ID it was
  // given (N40.1 pt 11).
  const matchByContentId = isSearchView || byContentId || Boolean(modalState.byContentId);

  const isSingleContentMode = !courseId;
  
  // Helper to check if user's audience type has access to content
  const isAudienceTypeAllowed = (contentAudienceType, userAudienceType) => {
    if (contentAudienceType === 3) return true;
    return parseInt(contentAudienceType) === parseInt(userAudienceType);
  };
  
  // Removed 3 Nov 2026 (N40.1 pt 13): needsManualCompletion, which only the Complete button used (the button is gone:
  // a document completes when the learner leaves it).

  const blockPlayerSaveActions = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();

      if (event.type === 'keydown') {
        const forbiddenKeys = ['s', 'S', 'p', 'P', 'u', 'U'];
        const isSaveOrPrint = (event.ctrlKey || event.metaKey) && forbiddenKeys.includes(event.key);
        const isF12 = event.key === 'F12';
        const isContextMenu = event.key === 'ContextMenu';

        if (isSaveOrPrint || isF12 || isContextMenu) {
          event.preventDefault();
          event.stopPropagation();
        }
      }
    }
  };

  const enrollOnPlay = async () => {
    if (!courseId) return;
    try {
      await fetch('/api/courses/enroll-on-content-access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ course_id: courseId })
      });
    } catch (err) {
      console.warn('Failed to enroll on content access:', err);
    }
  };

  // Play on the inline player enrols once (N4 pt 1, N40.1 pt 5). The Play button sits inside the clickable area, so one
  // click ran this twice and sent two enrol calls; the ref stops the second.
  const playStartedRef = useRef(false);
  const startVideoOnMountRef = useRef(false);
  const handlePlayerPlay = async () => {
    if (isInline && !hasStartedPlayback && !playStartedRef.current) {
      playStartedRef.current = true;
      playedContentIdRef.current = contentId != null ? String(contentId) : null;
      await enrollOnPlay();
      startVideoOnMountRef.current = true;
      setHasStartedPlayback(true);
      setIsPlaying(true);
    }
  };

  // Play on the inline player starts an uploaded video at once (N40.1 pt 12.1): once the video has mounted after Play,
  // start it; if the browser refuses, show it paused so the control matches. Removed 23 Oct 2026 (N40.1 pt 12.1):
  // mounting the video and marking it playing without starting it, so the learner saw the pause control on a stopped
  // video and had to press it twice.
  useEffect(() => {
    if (!hasStartedPlayback || !startVideoOnMountRef.current) return;
    startVideoOnMountRef.current = false; // once, in the render that mounts the content after Play
    const media = playerRef.current;
    if (typeof HTMLMediaElement === 'undefined' || !(media instanceof HTMLMediaElement)) return;
    const started = media.play();
    if (started && typeof started.catch === 'function') {
      started.catch(() => setIsPlaying(false));
    }
  });
  
  // Capture search context from modal state. The inline player under a search answer gets the cited page or
  // time as a prop, which comes first, so the answer opens at the passage it cites (K20.1.a pt 7). It also gets the
  // search's id, so the visit start and exit are saved on that search (N40.1 pt 11, K6.1 pt 9d); the "Was this
  // helpful?" step still needs the modal's search text, so it never shows under an answer that already asked.
  useEffect(() => {
    setSearchQuery(modalState?.searchQuery || null);
    setSearchQueryId(propQueryId || modalState?.queryId || null);
    setSeek(propSeek || modalState?.seek || null);
  }, [modalState?.searchQuery, modalState?.queryId, propSeek, propQueryId]);

  // load current user's audience_type from session (backend)
  useEffect(() => {
    const loadUser = async () => {
      try {
        const result = await authService.getCurrentUser();
        setUserAudienceType(result.data.user.audience_type || 0);
      } catch (err) {
        console.warn('Failed to fetch current user for audience_type', err);
        setUserAudienceType(0); // default to none
      }
    };
    loadUser();
  }, []);

  useEffect(() => {
    const loadFeedbackStatus = async () => {
      try {
        const response = await axios.get('/api/settings/feedback-status');
        setIsFeedbackEnabled(!!response.data?.enabled);
      } catch (err) {
        console.warn('Failed to fetch feedback status', err);
        setIsFeedbackEnabled(false);
      }
    };
    loadFeedbackStatus();
  }, []);

  useEffect(() => {
    if (isInline && !isModal) {
      // A video played for the item before stops: the next item's video only shows its frame until its Play (N40.1 pt 12.2).
      const media = playerRef.current;
      if (typeof HTMLMediaElement !== 'undefined' && media instanceof HTMLMediaElement && !media.paused) media.pause();
      setHasStartedPlayback(false);
      playStartedRef.current = false;
    } else {
      setHasStartedPlayback(true);
    }
  }, [contentId, isInline, isModal]);
  
  useEffect(() => {
    const fetchData  = async () => {
      try {
        setLoading(true);
        setIsAccessDenied(false);
        
        if (isSingleContentMode) {
          // Fetch only this single content
          const response = await axios.get(`/api/contents/${contentId}`);
          console.log('Fetched single content for single content mode', response.data);
          console.log('User audience type:', userAudienceType);
          setCurrentContent(response.data);          
          // Validate audience type
          if (!isAudienceTypeAllowed(response.data.audience_type, userAudienceType)) {
            setIsAccessDenied(true);
            setAccessDenialReason(`This content is for ${response.data.audience_type === 1 ? 'Internal' : 'External'} users only.`);
            return;
          }
          
          setCourseContents([]); // no sidebar
          setCurrentIndex(0);
        } else {
          const response = await axios.get(`/api/courses/${courseId}/contents`);
          const contents = response.data;
                
          // Find the module that holds the requested content (N40.1 pt 11): by its content ID when the player was given
          // one, else by the module's ID. Removed 2 Nov 2026 (N40.1 pt 11): matching every ID against the modules' IDs,
          // so a search answer, History or recent content opened the course's first module instead of the cited item.
          const index = contents.findIndex(content => (matchByContentId ? content.id : content.ccr_id) === parseInt(contentId));
          setCurrentIndex(index >= 0 ? index : 0);        
          const selectedContent = contents[index >= 0 ? index : 0];
          
          // Validate audience type
          if (!isAudienceTypeAllowed(selectedContent.audience_type, userAudienceType)) {
            setIsAccessDenied(true);
            setAccessDenialReason(`This content is for ${selectedContent.audience_type === 1 ? 'Internal' : 'External'} users only.`);
            return;
          }
          
          setCurrentContent(selectedContent);
          setContentCompleted(selectedContent?.completed === true || selectedContent?.completed_status == '1');
          setCourseContents(contents);
        }
      } catch (error) {
        console.error('Error fetching course contents:', error);
      } finally {
        setLoading(false);
      }
    };

    if (contentId && userAudienceType !== null) fetchData();
  }, [courseId, contentId, isSingleContentMode, userAudienceType]);
  
  useEffect(() => {
    if (currentContent) {
      const contentType = currentContent.type?.toLowerCase();
      const contentPath = currentContent.path;
      // Check if content is SCORM
      const isScormContent = contentType === 'scorm' || 
                    contentPath.includes('is_scorm=1') || 
                    contentPath.includes('scorm-player.html');
      setIsScorm(isScormContent);
    }
  }, [currentContent]);

  // Apply seek for videos after metadata is loaded. The inline player mounts an uploaded video before Play (N40.1 pt
  // 12.2), so the cited time applies then and Play starts there; before, an inline video always opened at 0:00 (K20.1.a
  // pt 7).
  useEffect(() => {
    if (isVideo && seek && seek.type === 'time' && playerRef.current) {
      const applySeek = () => {
        try {
          playerRef.current.currentTime = seek.seconds || 0;
        } catch (err) {
          console.warn('Failed to seek video:', err);
        }
      };

      if (playerRef.current.readyState >= 1) {
        applySeek();
        setSeek(null);
      } else {
        const onLoaded = () => {
          applySeek();
          setSeek(null);
          playerRef.current.removeEventListener('loadedmetadata', onLoaded);
        };
        playerRef.current.addEventListener('loadedmetadata', onLoaded);
      }
    }
  }, [isVideo, seek, currentContent, hasStartedPlayback]);
 
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // Video progress tracking for auto-completion (only in learner mode)
  useEffect(() => {
    if(isSingleContentMode) return;
    // Before Play the inline player only shows the video's frame, so nothing completes until Play (N40.1 pt 12.2).
    if (isInline && !hasStartedPlayback) return;
    if (isVideo && playerRef.current && isLearnerMode && !contentCompleted) {
      const handleTimeUpdate = () => {
        const video = playerRef.current;
        if (video && video.duration) {
          const progress = (video.currentTime / video.duration) * 100;
          setVideoProgress(progress);
          
          // Auto-complete when reaching 90% of the video
          if (progress >= 90 && !contentCompleted) {
            markContentAsCompleted(currentContent.ccr_id,currentContent.clr_id);
          }
        }
      };
      
      playerRef.current.addEventListener('timeupdate', handleTimeUpdate);
      
      return () => {
        if (playerRef.current) {
          playerRef.current.removeEventListener('timeupdate', handleTimeUpdate);
        }
      };
    }
  }, [isVideo, playerRef.current, isLearnerMode, contentCompleted, currentContent, isInline, hasStartedPlayback]);
  
  // The SCORM player's messages (S-35 pt 2), in this one listener: taken only from the player's own frame, and its
  // 'scorm-completed' completes only the module it names, when that is the module on screen. Removed 3 Nov 2026
  // (S-35 pt 2): a second listener, both taking the message from any window, and the second completing, when the
  // message named no module, the content's own ID, which could complete another module whose ID is the same number;
  // also its 'scorm-progress' branch, which only logged the message.
  useEffect(() => {
    if (isSingleContentMode || !isScorm || contentCompleted) return undefined;
    const handleScormMessage = (event) => {
      const frame = scormFrameRef.current;
      if (!frame || event.source !== frame.contentWindow || event.origin !== window.location.origin) return;
      const msg = event.data;
      if (!msg || msg.type !== 'scorm-completed') return;
      if (!currentContent?.ccr_id || String(msg.ccrId) !== String(currentContent.ccr_id)) return;
      markContentAsCompleted(currentContent.ccr_id, currentContent.clr_id).catch((error) => {
        console.error('Error updating SCORM status:', error);
      });
    };
    window.addEventListener('message', handleScormMessage);
    return () => window.removeEventListener('message', handleScormMessage);
  }, [isSingleContentMode, isScorm, contentCompleted, currentContent]);

  // Removed 3 Nov 2026 (N40.1 pt 13): completing a PDF, image or document as it opened (an effect on the current
  // content). Content that can't report its own completion completes when the learner leaves it (finishLeaving).

  // Removed 3 Nov 2026 (N40.1 pt 13.3): 'scorm-stopped' no longer completes the module. The SCORM player sends it
  // whenever a package's session ends, finished or not (leaving the player, moving to the next module); only
  // 'scorm-completed', sent when the package reports completed, completes it.

  // Removed 3 Nov 2026 (N40.1 pt 13): the quiz and assignment completion listener, which took ASSIGNMENT_COMPLETED and
  // QUIZ_COMPLETED from any window and completed whatever module was on screen (quizzes and assignments are retired,
  // K12 HIDE 1).

  useEffect(() => {
    if (!isLearnerMode) return;

    if (currentContent?.id) {
      axios.post('/api/contents/track-visit', {
        content_id: currentContent.id,
        content_type: currentContent.type,
        // query: searchQuery,
        query_id: searchQueryId
      }).catch(err => {
        console.warn('Failed to track visit:', err?.response?.data || err.message);
      });
    }
  }, [isLearnerMode, currentContent?.id]);

  // Helper function to show notifications
  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  // keep latestTimeRef up to date whenever currentTime changes
  useEffect(() => {
    latestTimeRef.current = currentTime || 0;
  }, [currentTime]);

  // The video or audio time on screen when the player records an exit (N40.1 pt 11), for Content Health's Exit point
  // (N5 pt 16). Only once the item has played, in every player (the media element's own play event): before Play the
  // inline player shows the frame at the cited time and saves nothing (N40.1 pt 12.2), and a pop-up player closed
  // without playing sends none either. A document's page can't be read from the browser's own PDF viewer, so it sends
  // none.
  const mediaPlayedIdRef = useRef(null);
  const exitPosition = () => (isVideo && currentContent && mediaPlayedIdRef.current === String(currentContent.id)
    ? Math.floor(latestTimeRef.current || 0) : undefined);

  useEffect(() => {
    latestDurationRef.current = duration || 0;
  }, [duration]);

  // save last-played when component unmounts or modal closes
  useEffect(() => {
    return () => {
      try {
        // Before Play the video only shows its frame at the cited time, so there is nothing to save (N40.1 pt 12.2).
        if (isVideo && currentContent && wasPlayed(currentContent) && (latestTimeRef.current > 0 || latestDurationRef.current > 0)) {
          saveVideoProgress({
            playedSeconds: latestTimeRef.current,
            durationSeconds: latestDurationRef.current
          });
        }
      } catch (e) { /* best-effort */ }
    };
  }, [isVideo, currentContent]);

  // Persist video progress to server
  const saveVideoProgress = async ({ playedSeconds = 0, durationSeconds = 0 }) => {
    console.log("--Indide Save", playedSeconds, durationSeconds);
   
    // avoid concurrent saves
    if (savingRef.current) return;
    savingRef.current = true;
    try {
      const percent = durationSeconds > 0 ? Math.round((playedSeconds / durationSeconds) * 10000) / 100 : 0;
      await axios.post('/api/learner/content/save-vid-progress', {
        content_id: currentContent.id,
        time_spent_seconds: Math.floor(playedSeconds* 100) / 100,
        duration_seconds: Math.floor(durationSeconds* 100) / 100 || 0,
        progress_percent: percent
      });
      console.debug('Saved video progress', {  playedSeconds, durationSeconds, percent });
    } catch (err) {
      console.warn('Failed to save video progress', err?.response?.data || err.message);
    } finally {
      savingRef.current = false;
    }
  };
  
  // Format time for video display (mm:ss)
  const formatTime = (timeInSeconds) => {
    if (!timeInSeconds) return '00:00';
    
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };
  
  // Play/pause video. Before Play on the inline player, the bar's play does what the gate's Play does (enrol, then start
  // the video), and skip and seek do nothing, as before the video showed its frame there (N40.1 pt 12.2).
  const beforeInlinePlay = isInline && !hasStartedPlayback;
  const togglePlay = () => {
    if (beforeInlinePlay) {
      handlePlayerPlay();
      return;
    }
    if (playerRef.current) {
      if (isPlaying) {
        playerRef.current.pause();
      } else {
        playerRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  
  // Mute/unmute video
  const toggleMute = () => {
    if (playerRef.current) {
      playerRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };
  
  // Handle volume change
  const handleVolumeChange = (event, newValue) => {
    if (playerRef.current) {
      playerRef.current.volume = newValue;
      setVolume(newValue);
      setIsMuted(newValue === 0);
    }
  };
  
  // Handle seeking in video
  const handleSeek = (event, newValue) => {
    if (beforeInlinePlay) return;
    if (playerRef.current) {
      playerRef.current.currentTime = newValue;
      setCurrentTime(newValue);
    }
  };

  const handleClose = () => {
    // save progress for current video before closing; before Play there is none (N40.1 pt 12.2)
    if (isVideo && currentContent && hasStartedPlayback) {
      saveVideoProgress({
        playedSeconds: latestTimeRef.current,
        durationSeconds: latestDurationRef.current
      });
    }
    // mark exit for current content
    if (isLearnerMode && currentContent?.id) {
      axios.post('/api/contents/track-visit', {
        content_id: currentContent.id,
        content_type: currentContent.type,
        action: 'exit',
        //query: searchQuery,
        query_id: searchQueryId,
        position_seconds: exitPosition() // N40.1 pt 11
      }).catch(err => console.warn('Failed to mark exit:', err?.response?.data || err.message));
    }
    
    // If opened from search query, show helpful feedback dialog before final exit
    if (searchQuery && searchQueryId) {
      setHelpfulDialogOpen(true);
      setPendingAction('exit');
      return;
    }

    finishLeaving(() => { // N40.1 pt 13
      // Call the refreshOnClose callback if provided
      if (refreshOnClose) {
        refreshOnClose();
      }
      if ((isModal || showCloseButton) && onClose) {
        onClose();
      } else {
        // Use returnUrl if provided, otherwise go back to course page
        navigate(returnUrl || `/courses/${courseId}`);
      }
    });
  };

  // Content in a course that the learner's leave can complete (N40.1 pt 13): in learner mode, not under a search
  // answer (the player completes nothing there yet), and not already complete. Content that can't report its own
  // completion (a document, an image, a YouTube video or another page) completes as it leaves; uploaded video and
  // audio complete at 90% watched, a SCORM package on its own report; quizzes and assignments are retired (K12 HIDE 1).
  const leaveCounts = isLearnerMode && !isSingleContentMode && !isSearchView && Boolean(currentContent) && !isContentComplete;
  const completesOnLeaving = leaveCounts && !isVideo && !isScorm && !['quiz', 'assignment'].includes(contentType);

  // Asks the SCORM package on screen to close, and waits for it (N40.1 pt 13.3): its page unloads first, so what it
  // reports as it closes (some packages report only then) completes the module before the player closes or moves on.
  // At most 2.5 seconds.
  const closeScormPackage = () => new Promise((resolve) => {
    const frame = scormFrameRef.current;
    if (!frame || !frame.contentWindow) { resolve(); return; }
    let timer = null;
    const done = () => {
      clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      resolve();
    };
    const onMessage = (event) => {
      if (event.source === frame.contentWindow && event.data && event.data.type === 'scorm-closed') done();
    };
    window.addEventListener('message', onMessage);
    timer = setTimeout(done, 2500);
    try {
      frame.contentWindow.postMessage({ type: 'scorm-close' }, window.location.origin);
    } catch (e) {
      done();
    }
  });

  // The finish function's last steps for content leaving the player (N40.1 pt 13): the close button, next or previous,
  // and another module from the list (each keeps its first steps, the video's position and the visit's exit). A SCORM
  // package closes first (13.3); content that can't report completes now; then the close or move waits for the
  // completion's reply (for up to 4 seconds: a slow reply doesn't hold the learner, and the module still completes)
  // and, when the course completes and its feedback form shows, for the learner to close the form (closing the player
  // took the form away). One leave runs at a time: a second click while one runs does nothing.
  const finishLeaving = async (then) => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    try {
      if (leaveCounts && isScorm) await closeScormPackage();
      if (completesOnLeaving) {
        leftCcrRef.current = currentContent.ccr_id;
        markContentAsCompleted(currentContent.ccr_id, currentContent.clr_id); // sets completionWorkRef
      }
      await Promise.race([completionWorkRef.current, new Promise((resolve) => { setTimeout(resolve, 4000); })]);
    } finally {
      leavingRef.current = false;
    }
    if (feedbackOpenRef.current) {
      afterFeedbackRef.current = then;
      return;
    }
    then();
  };

  const handleAccessDialougeClose = () => {
   onClose();
  };


  // In ContentPlayer.jsx
  useEffect(() => {
    return () => {
      // Call handleClose when component unmounts
      if (refreshOnClose) {
        console.log('Component unmounting, calling refreshOnClose');
        refreshOnClose();
      }
    };
  }, [refreshOnClose]);

  // Escape closes the player as its close button does (N40.1 pt 13): the content's leave runs, a SCORM package closes
  // first, and the page behind refreshes after the completion's reply. ContentPlayerModal no longer closes on Escape
  // itself (disableEscapeKeyDown), which closed the player without these steps, so the course page could show a
  // document left with Escape as not done. A pop-up over the player (the feedback form, 'Was this content helpful?')
  // takes Escape for itself first.
  const handleCloseRef = useRef(null);
  handleCloseRef.current = handleClose;
  useEffect(() => {
    if (!isModal) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== 'Escape' || event.repeat || event.defaultPrevented) return;
      if (handleCloseRef.current) handleCloseRef.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isModal]);

  // The player unmounting inside the app is a leave too (N40.1 pt 13) when no close or move ran for the content on
  // screen (a link out of the page): content that can't report completes, with the course's completion message (its
  // feedback form can't show once the player is gone). Closing the browser tab is not a leave.
  const unmountLeaveRef = useRef(null);
  useEffect(() => {
    unmountLeaveRef.current = completesOnLeaving
      ? { ccrId: currentContent.ccr_id, clrId: currentContent.clr_id, mark: contextMarkCompleted, showToast, isFeedbackEnabled }
      : null;
  });
  useEffect(() => () => {
    const leave = unmountLeaveRef.current;
    if (!leave || leftCcrRef.current === leave.ccrId) return;
    leave.mark(leave.ccrId, leave.clrId).then((response) => {
      if (response && response.status === 'completed') {
        leave.showToast(leave.isFeedbackEnabled
          ? 'Course completed successfully! You can provide feedback from your notifications.'
          : 'Course completed successfully!', 'success');
      }
    }).catch(() => {});
  }, []);
  
  // Skip forward 10 seconds
  const skipForward = () => {
    if (beforeInlinePlay) return;
    if (playerRef.current) {
      playerRef.current.currentTime = Math.min(playerRef.current.currentTime + 10, duration);
    }
  };
  
  // Skip backward 10 seconds
  const skipBackward = () => {
    if (beforeInlinePlay) return;
    if (playerRef.current) {
      playerRef.current.currentTime = Math.max(playerRef.current.currentTime - 10, 0);
    }
  };
  
  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (isFullscreen) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    } else {
      const element = containerRef.current;
      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      } else if (element.mozRequestFullScreen) {
        element.mozRequestFullScreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      }
    }
  };
  
  // Handle playback speed menu
  const handleSpeedMenuOpen = (event) => {
    setSpeedMenuAnchor(event.currentTarget);
  };
  
  const handleSpeedMenuClose = () => {
    setSpeedMenuAnchor(null);
  };
  
  const handlePlaybackRateChange = (rate) => {
    if (playerRef.current) {
      playerRef.current.playbackRate = rate;
      setPlaybackRate(rate);
    }
    handleSpeedMenuClose();
  };
  
  // Go to next content
  const goToNext = () => {
    setIsJustHovering(false);
    if (currentIndex < courseContents.length - 1) {
       // save current video progress before navigating
      if (isVideo && currentContent) {
        saveVideoProgress({
          playedSeconds: latestTimeRef.current,
          durationSeconds: latestDurationRef.current
        });
      }
      // mark exit for current content before navigating
      if (isLearnerMode && currentContent?.id) {
        axios.post('/api/contents/track-visit', {
          content_id: currentContent.id,
          content_type: currentContent.type,
          action: 'exit',
          // query: searchQuery,
          query_id: searchQueryId,
          position_seconds: exitPosition() // N40.1 pt 11
        }).catch(err => console.warn('Failed to mark exit:', err?.response?.data || err.message));
      }
      
      // If opened from search query, show helpful feedback dialog before proceeding to next
      if (searchQuery && searchQueryId) {
        setHelpfulDialogOpen(true);
        setPendingAction('next');
        return;
      }
      
      finishLeaving(() => playContent(currentIndex + 1)); // N40.1 pt 13
    }
  };
  
  const goToPrevious = () => {
    setIsJustHovering(false);
    if (currentIndex > 0) {
      if (isVideo && currentContent) {
        saveVideoProgress({
          playedSeconds: latestTimeRef.current,
          durationSeconds: latestDurationRef.current
        });
      }
      // mark exit for current content before navigating
      if (isLearnerMode && currentContent?.id) {
        axios.post('/api/contents/track-visit', {
          content_id: currentContent.id,
          content_type: currentContent.type,
          action: 'exit',
          // query: searchQuery,
          query_id: searchQueryId,
          position_seconds: exitPosition() // N40.1 pt 11
        }).catch(err => console.warn('Failed to mark exit:', err?.response?.data || err.message));
      }
      
      // If opened from search query, show helpful feedback dialog before proceeding to previous
      if (searchQuery && searchQueryId) {
        setHelpfulDialogOpen(true);
        setPendingAction('prev');
        return;
      }
      
      finishLeaving(() => playContent(currentIndex - 1)); // N40.1 pt 13
    }
  };
  
  // Play the content at the specified index
  const playContent = (index, loadContent = true) => {
    if (index >= 0 && index < courseContents.length) {
      setCurrentIndex(index);
      if (loadContent) {
        const newContent = courseContents[index];
        setCurrentContent(newContent);
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(0);
        setVideoProgress(0);
        
        // Reset completion state when content changes
        setContentCompleted(newContent?.completed === true || newContent?.completed_status === '1');
        
        // The same kind of ID the player was given, so finding the module again lands on this one (N40.1 pt 11).
        const newContentId = matchByContentId ? newContent.id : (newContent.ccr_id || newContent.id);
        
        if (isModal && updateCurrentContent) {
          updateCurrentContent(newContentId);
        }
        
        // Update URL without reloading
        if (!isModal) {
          navigate(`/courses/${courseId}/content/${newContentId}`, { replace: true });
        }
      }
    }
  };

  // Enhanced function to mark content as completed with learner mode support
  const markContentAsCompleted = async (ccrId, clrId = null) => {
    const completionKey = String(clrId || ccrId);
    if (isSearchView || contentCompleted || completionInFlightRef.current.has(completionKey)) return;

    completionInFlightRef.current.add(completionKey);
    let workDone = () => {};
    completionWorkRef.current = new Promise((resolve) => { workDone = resolve; }); // leaving waits for it (N40.1 pt 13)
    try {
      const contentClrId = clrId || currentContent?.clr_id;
      let response = null;

      // Only make API calls in learner mode, and only after the backend confirms success.
      if (isLearnerMode) {
        response = await contextMarkCompleted(ccrId, contentClrId);

        if (response && response.status === 'completed' && response.showFeedback) {
          setFeedbackData({
            itemId: response.itemId,
            enrollmentId: response.enrollmentId,
            instanceId: response.instanceId,
            itemType: response.itemType || 'course'
          });

          feedbackOpenRef.current = true; // a close or move waits for the form (N40.1 pt 13)
          setFeedbackModalOpen(true);
        } else if (response && response.status === 'completed' && !completionNoticeShownRef.current.has(completionKey)) {
          completionNoticeShownRef.current.add(completionKey);
          showToast(
            isFeedbackEnabled
              ? 'Course completed successfully! You can provide feedback from your notifications.'
              : 'Course completed successfully!',
            'success'
          );
        }
      }

      if (response && response.status === 'error') {
        throw new Error(response.message || 'Completion failed');
      }

      // Update the UI to show completion only after the backend confirms success
      setContentCompleted(true);

      const updatedContents = courseContents.map(content => {
        if (content.ccr_id == ccrId) {
          return {
            ...content,
            completed: true,
            completed_status: '1'
          };
        }
        return content;
      });

      setCourseContents(updatedContents);

      if (isLearnerMode) {
        setCompletionDialog(true);
      }
    } catch (error) {
      console.error('Error marking content as completed:', error);
      showNotification('Error marking content as completed', 'error');
    } finally {
      completionInFlightRef.current.delete(completionKey);
      workDone();
    }
  };

  // Handle feedback modal close
  const handleFeedbackModalClose = () => {
    setFeedbackModalOpen(false);
    setFeedbackData(null);
    feedbackOpenRef.current = false;
    // A close or move that waited for the form goes ahead now (N40.1 pt 13).
    const then = afterFeedbackRef.current;
    afterFeedbackRef.current = null;
    if (then) then();
  };

  // Removed 3 Nov 2026 (N40.1 pt 13): handleManualCompletion, a handler for the Complete button that nothing called
  // (the button had its own), now gone with the button.
  
  // Save helpful feedback from search and continue with pending action
  const saveHelpfulFeedback = async (isHelpful) => {
    try {
      // Save the helpful feedback to the server
      // One feedback endpoint for every search surface (N4 pt 3).
      const response = await axios.post('/api/user-queries/feedback', {
        queryId: searchQueryId,
        intent: isHelpful ? 'yes' : 'no'
      });
      
      if (response.data.success) {
        showNotification(
          isHelpful ? 'Thank you! Glad we could help.' : 'Thank you for your feedback.',
          'success'
        );
      }
    } catch (error) {
      console.error('Error saving helpful feedback:', error);
      showNotification('Error saving feedback. Please try again.', 'error');
    }
    
    // Close the dialog and proceed with pending action
    setHelpfulDialogOpen(false);
    
    // Execute the pending action (note: video save and exit tracking already done before dialog)
    if (pendingAction === 'exit') {
      // Perform final exit - all save/exit tracking already done
      if (refreshOnClose) {
        refreshOnClose();
      }
      if (isModal && onClose) {
        onClose();
      } else {
        navigate(returnUrl || `/courses/${courseId}`);
      }
    } else if (pendingAction === 'next') {
      // All save/exit tracking already done, just navigate to next
      if (currentIndex < courseContents.length - 1) {
        playContent(currentIndex + 1);
      }
    } else if (pendingAction === 'prev') {
      // All save/exit tracking already done, just navigate to previous
      if (currentIndex > 0) {
        playContent(currentIndex - 1);
      }
    }
    
    // Clear pending action
    setPendingAction(null);
  };
  
  // Get icon for content type
  const getContentIcon = (type) => {
    if (!type) return <Description />;
    
    switch (type.toLowerCase()) {
      case 'video':
        return <PlayArrow />;
      case 'pdf':
        return <PictureAsPdf />;
      case 'image':
        return <Image />;
      case 'assignment':
        return <Assignment />;
      case 'quiz':
        return <Quiz />;
      case 'scorm':
        return <Description />;
      default:
        return <InsertDriveFile />;
    }
  };

  // Render sidebar
  const renderSidebar = () => {
    return (
      <div className={`sidebar-container ${sidebarOpen ? 'open' : ''}`}>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" className="sidebar-title">Course Content</Typography>
            <IconButton onClick={() => setSidebarOpen(false)} sx={{ color: tokens.color.contrastText }}>
              <Close />
            </IconButton>
          </Box>
          <List>
            {courseContents.map((content, index) => {
              // Check if this content is completed
              const isCompleted = content.completed_status == '1' || content.completed === true;
                
              return (
                <ListItem 
                  key={content.ccr_id || content.id || index}
                  button
                  selected={currentIndex === index}
                  onClick={() => {
                    // Another module leaves the one on screen first (N40.1 pt 13); the one on screen just plays.
                    if (index === currentIndex) playContent(index);
                    else finishLeaving(() => playContent(index));
                  }}
                  className={`sidebar-list-item ${currentIndex === index ? 'selected' : ''}`}
                >
                  <ListItemIcon sx={{ color: tokens.color.contrastText, minWidth: 36 }}>
                    {getContentIcon(content.type)}
                  </ListItemIcon>
                  <ListItemText 
                    primary={content.title || content.name}
                    secondary={content.type ? (content.type.charAt(0).toUpperCase() + content.type.slice(1)) : 'Content'}
                    primaryTypographyProps={{ className: 'sidebar-list-item-text' }}
                    secondaryTypographyProps={{ className: 'sidebar-list-item-subtext' }}
                  />
                  {isCompleted && (
                    <CheckCircle sx={{ color: tokens.color.positive, ml: 1 }} fontSize="small" />
                  )}
                </ListItem>
              );
            })}
          </List>
        </Box>
      </div>
    );
  };
  
  // What the SCORM player is told as its module opens (N40.2 pts 7, 7.2): whether it may record a completion itself as
  // the page goes, only where the app records one (learner mode, a course's module, not under a search answer, not one
  // already complete), and the path the app's own completion names. Worked out once per module on screen, whatever its
  // type, so the frame's address stays the same, and the package doesn't reload, when the module completes during play,
  // and a module left and opened again is worked out again.
  const scormParamsFor = (content) => {
    const key = content ? `${content.ccr_id ?? ''}|${content.id ?? ''}` : null;
    if (scormParamsRef.current.key !== key) {
      const track = isLearnerMode && !isSingleContentMode && !isSearchView && !isContentComplete;
      const pathId = modalState?.lpathId || 0;
      scormParamsRef.current = { key, params: `&track=${track ? 1 : 0}&lpathId=${encodeURIComponent(pathId)}` };
    }
    return scormParamsRef.current.params;
  };
  const scormParams = scormParamsFor(currentContent);

  // Render content player based on content type
  const renderContentPlayer = () => {
    if (isJustHovering) {
        return currentContent ? (
          <div className="content-placeholder" style={{ width: '100%', height: '100%' }}>
            {/* This is just a placeholder while hovering */}
          </div>
        ) : null;
    }

    if (!currentContent) return null;
    
    const contentPath = currentContent.path;
    // For YouTube videos
    if (contentPath && (contentPath.includes('youtube.com/embed') || contentPath.includes('youtu.be'))) {
        return (
            <div
                onContextMenu={blockPlayerSaveActions}
                onKeyDown={blockPlayerSaveActions}
                style={{ width: '100%', height: '100%' }}
            >
              <iframe 
                  src={contentPath}
                  className="content-frame"
                  allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  onContextMenu={blockPlayerSaveActions}
                  // Removed 3 Nov 2026 (N40.1 pt 13): completing a YouTube video a minute after it loaded; it completes
                  // when the learner leaves it (finishLeaving).
              />
            </div>
        );
    }
    
    // For regular video content
    if (isVideo) { 
        return (
            <div
                onContextMenu={blockPlayerSaveActions}
                onKeyDown={blockPlayerSaveActions}
                style={{ width: '100%', height: '100%' }}
            >
              <video
                  ref={playerRef}
                  src={contentPath}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  controlsList="nodownload"
                  disablePictureInPicture
                  onContextMenu={blockPlayerSaveActions}
                  onTimeUpdate={(e) => { 
                    setCurrentTime(e.target.currentTime); 
                    // Before Play the inline player only shows the frame at the cited time (N40.1 pt 12.2): nothing is
                    // saved and nothing completes until the learner presses Play.
                    if (isInline && !hasStartedPlayback) return;
                    // (example: every 5s)
                    if (Math.floor(e.target.currentTime) %5 === 0) {
                      saveVideoProgress({
                        playedSeconds: e.target.currentTime,
                        durationSeconds: e.target.duration
                      });
                    }
                    // Auto-complete when reaching 90% of the video (only in learner mode)
                    if (isLearnerMode && !contentCompleted && e.target.currentTime / e.target.duration >= 0.9) {
                      markContentAsCompleted(currentContent.ccr_id,currentContent.clr_id);
                    }
                  }}
                  onDurationChange={(e) => setDuration(e.target.duration)}
                  onEnded={() => {
                      setIsPlaying(false);
                      // final save on ended
                      saveVideoProgress({
                        playedSeconds: latestTimeRef.current || duration,
                        durationSeconds: latestDurationRef.current || duration
                      });
                      if (isLearnerMode && !contentCompleted) {
                        markContentAsCompleted(currentContent.ccr_id,currentContent.clr_id);
                      }
                  }}
                  onPlay={() => {
                    setIsPlaying(true);
                    if (currentContent) mediaPlayedIdRef.current = String(currentContent.id); // N40.1 pt 11
                  }}
                  onPause={() => setIsPlaying(false)}
                  playbackRate={playbackRate}
                  muted={isMuted}
                  volume={volume}
              />
            </div>
        );
    }
    
    // For image content
    if (contentType === 'image' || /\.(jpg|jpeg|png|gif|svg)$/i.test(contentPath)) {
        return (
            <Box 
                sx={{ 
                    width: '100%', 
                    height: '100%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    backgroundColor: '#000'
                }}
                onContextMenu={blockPlayerSaveActions}
                onKeyDown={blockPlayerSaveActions}
            >
                <img 
                    src={contentPath} 
                    alt={currentContent.title} 
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} 
                    draggable={false}
                    onContextMenu={blockPlayerSaveActions}
                    // Removed 3 Nov 2026 (N40.1 pt 13): completing an image 5 seconds after it loaded; it completes
                    // when the learner leaves it (finishLeaving).
                />
            </Box>
        );
    }
    
    // For PDF content
    if (contentType === 'pdf' || contentPath.endsWith('.pdf')) {
        const iframeSrc = contentPath + (seek && seek.type === 'page' && seek.pageNumber ? `#page=${seek.pageNumber}` : '');
        return (
            <div
                onContextMenu={blockPlayerSaveActions}
                onKeyDown={blockPlayerSaveActions}
                style={{ width: '100%', height: '100%' }}
            >
              <iframe 
                  src={iframeSrc}
                  className="content-frame"
                  onContextMenu={blockPlayerSaveActions}
                  // Removed 3 Nov 2026 (N40.1 pt 13): completing a PDF 30 seconds after it loaded; it completes when
                  // the learner leaves it (finishLeaving).
                  type="application/pdf"
                  allowFullScreen
              />
            </div>
        );
    }

    // For Office documents (Word, Excel, PowerPoint)
    if (contentType === 'document' || 
        /\.(doc|docx|xls|xlsx|ppt|pptx|txt|rtf|csv)$/i.test(contentPath)) {
        
        // Get the file extension to determine file type
        const fileExtension = contentPath.split('.').pop().toLowerCase();
        
        // Use our DocumentViewer component for supported file types
        if (/^(docx|xlsx|xls|csv|txt|ppt|pptx)$/.test(fileExtension)) {
            return (
                <div
                    onContextMenu={blockPlayerSaveActions}
                    onKeyDown={blockPlayerSaveActions}
                    style={{ width: '100%', height: '100%' }}
                >
                    <DocumentViewer 
                        filePath={contentPath}
                        fileType={fileExtension}
                        onLoad={() => {
                          // Removed 3 Nov 2026 (N40.1 pt 13): completing a document 30 seconds after it loaded; it
                          // completes when the learner leaves it (finishLeaving).

                          // If there's a seek page for documents, try to scroll the rendered doc to that page
                          if (seek && seek.type === 'page' && seek.pageNumber) {
                            setTimeout(() => {
                              const docEl = document.querySelector('.docx-container');
                              if (docEl) {
                                const pagesEstimate = Math.max(1, Math.round(docEl.scrollHeight / docEl.clientHeight));
                                const targetScroll = Math.max(0, (seek.pageNumber - 1) * docEl.clientHeight);
                                docEl.scrollTop = Math.min(targetScroll, docEl.scrollHeight);
                              }
                              // clear seek after applying
                              setSeek(null);
                            }, 200);
                          }
                        }}
                    />
                </div>
            );
        }
        
        // For unsupported document types, offer download option
        return (
            <Box 
                sx={{ 
                    width: '100%', 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center', 
                    justifyContent: 'center',
                    backgroundColor: tokens.color.bgPrimary,
                    p: 3
                }}
            >
                <Typography variant="h6" gutterBottom>
                    This file type may not display properly in the browser.
                </Typography>
                <Button 
                    variant="contained" 
                    href={contentPath} 
                    target="_blank" 
                    download
                    // Removed 3 Nov 2026 (N40.1 pt 13): completing the file when Download was pressed; it completes
                    // when the learner leaves it (finishLeaving).
                    sx={{ mt: 2 }}
                >
                    Download File
                </Button>
            </Box>
        );
    }
    
    // For assignments
    if (contentType === 'assignment') {
      return (
          <div
              onContextMenu={blockPlayerSaveActions}
              onKeyDown={blockPlayerSaveActions}
              style={{ width: '100%', height: '100%' }}
          >
            <iframe 
                src={`/assignments/preview/${currentContent.id}/${courseId}/${currentContent.ccr_id}`}
                className="content-frame"
                onContextMenu={blockPlayerSaveActions}
                onLoad={() => {                
                  // For assignments, we'll rely on the assignment completion API
                  // which will be handled separately
                }}
            />
          </div>
      );
  }
    
    // For quizzes
    if (contentType === 'quiz') {
        return (
            <div
                onContextMenu={blockPlayerSaveActions}
                onKeyDown={blockPlayerSaveActions}
                style={{ width: '100%', height: '100%' }}
            >
              <iframe 
                  src={`/quiz/preview/${currentContent.id}/${courseId}/${currentContent.ccr_id}`}
                  className="content-frame"
                  onContextMenu={blockPlayerSaveActions}
                  onLoad={() => {
                    // For quizzes, we'll rely on the quiz completion API
                    // which will be handled separately
                  }}
              />
            </div>
        );
    }
    
    // Default: use iframe for all other content types
    return (
        <div
            onContextMenu={blockPlayerSaveActions}
            onKeyDown={blockPlayerSaveActions}
            style={{ width: '100%', height: '100%' }}
        >
          <iframe 
              ref={scormFrameRef} // a SCORM package plays in this frame; leaving asks it to close (N40.1 pt 13.3)
              src={contentPath.includes('scorm-player.html') ? `${contentPath}${scormParams}` : contentPath}
              className="content-frame"
              title={currentContent.title}
              onContextMenu={blockPlayerSaveActions}
              onLoad={() => {
                setLoading(false);
                // Removed 3 Nov 2026 (N40.1 pt 13): completing any other page 30 seconds after it loaded; it completes
                // when the learner leaves it (finishLeaving).
              }}
          />
        </div>
    );
  };
  
  return (
    <div className={`player-container${isInline ? ' inline' : ''}`} ref={containerRef}>
      {!isSingleContentMode && !hideCourseNavigation && renderSidebar()}
      
      <div className={`content-container ${sidebarOpen ? 'sidebar-open' : ''}`}>
        {/* Top controls bar with title and preview mode indicator */}
        <div className="top-controls">
            {/* The title uses the room the bar has before it is cut with '...' (N40.1 pt 13.2), and it is the one
                place the content's title shows (N40.1 pt 12.3). Removed 2 Nov 2026 (N40.1 pt 13.2): cutting it at 80%
                of its box, so a title was cut with room to spare. */}
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ color: tokens.color.contrastText, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {currentContent?.title || currentContent?.name}
                </Typography>
                {!isLearnerMode && (
                  <Chip 
                    label="Preview Mode" 
                    size="small" 
                    color="primary" 
                    sx={{ ml: 2, flexShrink: 0, backgroundColor: 'rgba(255, 255, 255, 0.2)', color: tokens.color.contrastText, height: 24 }}
                  />
                )}
                {isContentComplete && (
                  <Chip 
                    icon={<CheckCircle fontSize="small" />}
                    label="Completed" 
                    size="small" 
                    color="success" 
                    sx={{ ml: 2, flexShrink: 0, height: 24 }}
                  />
                )}
            </Box>
          
            {(isModal || showCloseButton) && (
              <IconButton 
                  onClick={handleClose}
                  sx={{ color: tokens.color.contrastText }}
                  aria-label="Close"
              >
                  <Close />
              </IconButton>
            )}
        </div>

        {/* Main content area */}
        <div className="content-area">
          {/* Before Play, the inline player shows an uploaded video's frame at the cited time and its length (N40.1 pt
              12.2): the video mounts with the gate over it, in the place it then plays in, so Play starts the same
              element at that time. Removed 2 Nov 2026 (N40.1 pt 12.2): mounting the video only after Play, so the gate
              sat on black and the time read 00:00 / 00:00. */}
          {!isAccessDenied && (!isInline || hasStartedPlayback || showFrameBeforePlay) && renderContentPlayer()}
          {!isAccessDenied && isInline && !hasStartedPlayback && (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0,0,0,0.12)',
                borderRadius: '12px',
                position: showFrameBeforePlay ? 'absolute' : 'relative',
                inset: showFrameBeforePlay ? 0 : 'auto',
                p: 3,
                textAlign: 'center'
              }}
              onClick={handlePlayerPlay}
            >
              <Box sx={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.25)', borderRadius: '12px' }} />
              <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                {/* 'Open' for a document, 'Play' for media (N40.1 pt 12.3). Removed 2 Nov 2026 (N40.1 pt 12.3): 'Play'
                    on every type, and the content's title again under the button (it shows in the top bar). */}
                <Button
                  variant="contained"
                  size="large"
                  startIcon={isDocument ? undefined : <PlayArrow />}
                  onClick={handlePlayerPlay}
                >
                  {isDocument ? 'Open' : 'Play'}
                </Button>
              </Box>
            </Box>
          )}
        </div>

        {/* Bottom controls container */}
        <div className="bottom-controls" style={{ zIndex: isScorm ? 9 : 10 }}>
          {/* Progress bar (only for videos) */}
          {isVideo && (
            <Box sx={{ width: '100%', mb: 1 }}>
              <Slider
                value={currentTime}
                max={duration || 100}
                onChange={handleSeek}
                aria-label="Video progress"
                sx={{
                  color: '#f00',
                  height: 4,
                  '& .MuiSlider-thumb': {
                    width: 10,
                    height: 10,
                    transition: '0.3s cubic-bezier(.47,1.64,.41,.8)',
                    '&:hover, &.Mui-focusVisible': {
                      boxShadow: '0px 0px 0px 8px rgba(255, 0, 0, 0.16)'
                    }
                  },
                  '& .MuiSlider-rail': {
                    opacity: 0.28,
                  }
                }}
              />
            </Box>
          )}
          
          {/* Controls */}
          <div className="controls-row">
            {/* Left controls */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {!isSingleContentMode && !hideCourseNavigation && (
                <IconButton 
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  sx={{ color: tokens.color.contrastText }}
                >
                  <MenuIcon />
                </IconButton>
              )}

              {isVideo && (
                <span className="time-display">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
              )}
              {/* Under an uploaded video in a course, until it is complete (it completes at 90% watched, N40.1 pts 13,
                  13.1); standalone content has no completion, so it says nothing. Not under a search answer yet: there the
                  player completes nothing (markContentAsCompleted) until N40.1 point 13's finish function. */}
              {isVideo && !isAudioMedia && !isSingleContentMode && isLearnerMode && !isContentComplete && !isSearchView && (
                <span className="time-display" style={{ marginLeft: '12px' }}>Watch 90% to complete</span>
              )}
            </Box>
            
            {/* Center controls */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {!isSingleContentMode && !hideCourseNavigation && (
                <div className="nav-button nav-button-prev">
                  {currentIndex > 0 && (
                    <span 
                      className="nav-text prev-text"
                    >
                      {courseContents[currentIndex - 1]?.title || courseContents[currentIndex - 1]?.name || 'Previous'}
                    </span>
                  )}
                  <IconButton 
                    onClick={goToPrevious}
                    disabled={currentIndex === 0}
                    sx={{ color: tokens.color.contrastText }}
                  >
                    <SkipPrevious />
                  </IconButton>
                </div>
              )}
              {isVideo && (
                <>
                  <IconButton onClick={skipBackward} sx={{ color: tokens.color.contrastText }}>
                    <FastRewind />
                  </IconButton>
                  
                  <IconButton onClick={togglePlay} sx={{ color: tokens.color.contrastText, mx: 1 }}>
                    {isPlaying ? <Pause /> : <PlayArrow />}
                  </IconButton>
                  
                  <IconButton onClick={skipForward} sx={{ color: tokens.color.contrastText }}>
                    <FastForward />
                  </IconButton>
                </>
              )}
              {!isSingleContentMode && !hideCourseNavigation && (
                <div className="nav-button nav-button-next">
                  <IconButton 
                    onClick={goToNext}
                    disabled={currentIndex === courseContents.length - 1}
                    sx={{ color: tokens.color.contrastText }}
                  >
                    <SkipNext />
                  </IconButton>
                  {currentIndex < courseContents.length - 1 && (
                    <span 
                      className="nav-text next-text"
                    >
                      {courseContents[currentIndex + 1]?.title || courseContents[currentIndex + 1]?.name || 'Next'}
                    </span>
                  )}
                </div>
              )}
            </Box>
            
            {/* Right controls */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {isVideo && (
                <>
                  <div className="volume-control">
                    <IconButton onClick={toggleMute} sx={{ color: tokens.color.contrastText }}>
                      {isMuted ? <VolumeOff /> : <VolumeUp />}
                    </IconButton>
                    <Slider
                      value={isMuted ? 0 : volume}
                      onChange={handleVolumeChange}
                      min={0}
                      max={1}
                      step={0.01}
                      aria-label="Volume"
                      sx={{
                        color: tokens.color.contrastText,
                        width: 80,
                        '& .MuiSlider-track': {
                          border: 'none',
                        },
                        '& .MuiSlider-thumb': {
                          width: 10,
                          height: 10,
                          backgroundColor: tokens.color.contrastText,
                        },
                      }}
                    />
                  </div>
                  
                  <IconButton onClick={handleSpeedMenuOpen} sx={{ color: tokens.color.contrastText }}>
                    <Speed />
                  </IconButton>
                  <Menu
                    anchorEl={speedMenuAnchor}
                    open={Boolean(speedMenuAnchor)}
                    onClose={handleSpeedMenuClose}
                    PaperProps={{
                      sx: {
                        backgroundColor: '#1a1a1a',
                        color: tokens.color.contrastText
                      }
                    }}
                  >
                    {[0.5, 0.75, 1, 1.25, 1.5, 2].map((rate) => (
                      <MenuItem 
                        key={rate} 
                        onClick={() => handlePlaybackRateChange(rate)}
                        selected={playbackRate === rate}
                        sx={{
                          backgroundColor: playbackRate === rate ? 'rgba(255,255,255,0.1)' : 'transparent',
                          '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.05)'
                          }
                        }}
                      >
                        {rate === 1 ? 'Normal' : `${rate}x`}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
              
              {/* Removed 3 Nov 2026 (N40.1 pt 13): the Complete button for documents, images and pages; they complete
                  when the learner leaves them (finishLeaving). */}
              
              <IconButton onClick={toggleFullscreen} sx={{ color: tokens.color.contrastText }}>
                {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
              </IconButton>
            </Box>
          </div>
        </div>
      </div>      
            
      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setNotification(prev => ({ ...prev, open: false }))} 
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {/* "Was this helpful?" Dialog for search intent resolution */}
      <Dialog
        open={helpfulDialogOpen}
        onClose={() => {
          setHelpfulDialogOpen(false);
          setPendingAction(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Was this content helpful?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 2 }}>
            You searched for: <strong>"{searchQuery}"</strong>
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Did this content help you find what you were looking for?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="outlined"
            onClick={() => saveHelpfulFeedback(false)}
          >
            No
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => saveHelpfulFeedback(true)}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {feedbackModalOpen && feedbackData && (
        <FeedbackModal
          open={feedbackModalOpen}
          onClose={handleFeedbackModalClose}
          itemId={feedbackData.itemId}
          enrollmentId={feedbackData.enrollmentId}
          instanceId={feedbackData.instanceId}
          itemType={feedbackData.itemType}
        />
      )}      
      
      <Dialog
        open={isAccessDenied}
        onClose={handleAccessDialougeClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#f44336', color: tokens.color.contrastText, fontWeight: 'bold' }}>
          Access Denied
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1">
            {accessDenialReason}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="contained"
            color="primary outlined"
            onClick={handleAccessDialougeClose}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ContentPlayer;